import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const STATUS_STYLES = {
  queued:     { label: "I kö",       cls: "bg-slate-100 text-slate-700" },
  rendering:  { label: "Renderar",   cls: "bg-purple-100 text-purple-700" },
  rendered:   { label: "Renderad",   cls: "bg-teal-100 text-teal-700" },
  creating:   { label: "Skapar",     cls: "bg-blue-100 text-blue-700" },
  processing: { label: "Bearbetar",  cls: "bg-amber-100 text-amber-700" },
  publishing: { label: "Publicerar", cls: "bg-indigo-100 text-indigo-700" },
  published:  { label: "Publicerad", cls: "bg-emerald-100 text-emerald-700" },
  failed:     { label: "Misslyckad", cls: "bg-red-100 text-red-700" },
};

const LANDING_PRESETS = [
  { slug: "tidrapportering-bygg",              title: "Tidrapportering för bygg",              subtitle: "Personalliggare inkluderad. Kom igång på 5 minuter." },
  { slug: "tidrapportering-snickeri",          title: "Tidrapportering för snickeri",          subtitle: "Byggd för verkstan och byggplatsen." },
  { slug: "tidrapportering-vvs",               title: "Tidrapportering för VVS",               subtitle: "ROT-avdrag, kunder och timmar på ett ställe." },
  { slug: "tidrapportering-maleri",            title: "Tidrapportering för måleri",            subtitle: "Perfekt för mindre måleri-firmor." },
  { slug: "tidrapportering-fastighetsservice", title: "Tidrapportering för fastighetsservice", subtitle: "Ronder, fel & kunder — samlat." },
];

export default function ReelsPublishPanel({ connected }) {
  const [jobs, setJobs] = useState([]);
  const [imageUrl, setImageUrl] = useState("");
  const [caption, setCaption] = useState("");
  const [posting, setPosting] = useState(false);
  const [loading, setLoading] = useState(false);

  // Video-rendering
  const [videoSlug, setVideoSlug] = useState(LANDING_PRESETS[0].slug);
  const [videoTitle, setVideoTitle] = useState(LANDING_PRESETS[0].title);
  const [videoSubtitle, setVideoSubtitle] = useState(LANDING_PRESETS[0].subtitle);
  const [videoCaption, setVideoCaption] = useState("");
  const [panSeconds, setPanSeconds] = useState(15);
  const [rendering, setRendering] = useState(false);
  const [lastRender, setLastRender] = useState(null); // { video_url }

  // Batch: publicera alla 10 branscher
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchSkipExisting, setBatchSkipExisting] = useState(true);
  const [batchDelaySec, setBatchDelaySec] = useState(60);
  const [batchDryRun, setBatchDryRun] = useState(false);
  const [batchInfo, setBatchInfo] = useState(null); // { industries, estimated_minutes }

  const onPresetChange = (slug) => {
    setVideoSlug(slug);
    const preset = LANDING_PRESETS.find((p) => p.slug === slug);
    if (preset) {
      setVideoTitle(preset.title);
      setVideoSubtitle(preset.subtitle);
    }
  };

  const loadJobs = useCallback(async () => {
    if (!connected) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/reels/jobs?limit=20`, { withCredentials: true });
      setJobs(res.data.jobs || []);
    } catch {
      // tyst — panelen visas ändå
    } finally {
      setLoading(false);
    }
  }, [connected]);

  useEffect(() => { loadJobs(); }, [loadJobs]);

  const publishImage = async () => {
    if (!imageUrl.trim()) {
      toast.error("Ange en publik bild-URL");
      return;
    }
    if (!/^https:\/\//i.test(imageUrl.trim())) {
      toast.error("Bild-URL:en måste börja med https://");
      return;
    }
    setPosting(true);
    try {
      const res = await axios.post(
        `${API_BASE}/api/reels/publish-image`,
        { image_url: imageUrl.trim(), caption },
        { withCredentials: true }
      );
      toast.success(`Publicerad! Media ID: ${res.data.media_id}`);
      setImageUrl("");
      setCaption("");
      loadJobs();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error(`Kunde inte publicera: ${msg}`);
    } finally {
      setPosting(false);
    }
  };

  const deleteJob = async (id) => {
    if (!window.confirm("Ta bort detta jobb ur historiken? (IG-posten påverkas eller ej)")) return;
    try {
      await axios.delete(`${API_BASE}/api/reels/jobs/${id}`, { withCredentials: true });
      loadJobs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunde inte ta bort");
    }
  };

  const renderVideo = async ({ dryRun }) => {
    if (!videoTitle.trim()) {
      toast.error("Ange en rubrik för slutkortet");
      return;
    }
    setRendering(true);
    setLastRender(null);
    const t0 = Date.now();
    try {
      const landingUrl = `${API_BASE}/${videoSlug}`.replace(/\/+$/, "");
      const res = await axios.post(
        `${API_BASE}/api/reels/render-and-publish`,
        {
          landing_url: landingUrl,
          end_title: videoTitle,
          end_subtitle: videoSubtitle,
          caption: videoCaption,
          pan_seconds: panSeconds,
          dry_run: !!dryRun,
        },
        { withCredentials: true, timeout: 240_000 }
      );
      const seconds = Math.round((Date.now() - t0) / 1000);
      setLastRender({ video_url: res.data.video_url, media_id: res.data.media_id, permalink: res.data.permalink });
      if (dryRun) {
        toast.success(`Video renderad på ${seconds}s — granska först innan publicering`);
      } else {
        toast.success(`Reel publicerat! ${seconds}s (rendering + upload + Meta-processing)`);
      }
      loadJobs();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error(`Rendering misslyckades: ${msg}`);
    } finally {
      setRendering(false);
    }
  };

  const publishAllIndustries = async () => {
    setBatchRunning(true);
    setBatchInfo(null);
    try {
      const res = await axios.post(
        `${API_BASE}/api/reels/publish-all-industries`,
        {
          landing_base_url: "https://app.trappkonsulten.se",
          delay_between_seconds: batchDelaySec,
          skip_existing: batchSkipExisting,
          dry_run: batchDryRun,
        },
        { withCredentials: true }
      );
      setBatchInfo({
        industries: res.data.industries,
        count: res.data.count,
        estimated_minutes: res.data.estimated_minutes,
        started_at: Date.now(),
      });
      toast.success(`🚀 Batch startad — ${res.data.count} reels, ~${res.data.estimated_minutes} min`);
      loadJobs();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error(`Batch-fel: ${msg}`);
      setBatchRunning(false);
    }
  };

  // Auto-refresh jobs var 15:e sekund när batch körs
  useEffect(() => {
    if (!batchRunning) return undefined;
    const iv = setInterval(loadJobs, 15000);
    return () => clearInterval(iv);
  }, [batchRunning, loadJobs]);

  // Stanna auto-refresh när batch är klar (inga aktiva jobb med batch_slug + tid gått ut)
  useEffect(() => {
    if (!batchRunning || !batchInfo) return;
    const activeStatuses = ["rendering", "creating", "processing", "publishing", "queued"];
    const active = jobs.filter((j) => activeStatuses.includes(j.status));
    const elapsed = (Date.now() - batchInfo.started_at) / 60000; // minutes
    // Sluta polla när: inget aktivt OCH minst 2 min gått (grace period)
    if (active.length === 0 && elapsed > 2) {
      setBatchRunning(false);
      toast.success("✅ Batch klar — kolla historiken");
    }
  }, [jobs, batchRunning, batchInfo]);

  if (!connected) return null;

  return (
    <div className="mt-6 space-y-4" data-testid="reels-publish-panel">
      {/* Rendera Reel-video från landningssida */}
      <div className="bg-surface border border-app rounded-lg p-5">
        <h4 className="text-base font-semibold text-text-primary">Rendera Reel-video från landningssida</h4>
        <p className="text-sm text-text-muted mt-1">
          Playwright renderar sidan → FFmpeg gör en 9:16 panorering + slutkort → publiceras som Reel på @trappkonsulten__.
          Testa alltid med &quot;Rendera dry-run&quot; först och granska videon innan publicering.
        </p>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-widest text-text-muted">Landningssida</label>
            <select
              value={videoSlug}
              onChange={(e) => onPresetChange(e.target.value)}
              className="mt-1 w-full border border-app rounded px-3 py-2 text-sm bg-app"
              data-testid="reels-video-slug-select"
            >
              {LANDING_PRESETS.map((p) => (
                <option key={p.slug} value={p.slug}>{p.slug}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-text-muted">Panoreringstid (sek)</label>
            <input
              type="number"
              min={8}
              max={45}
              value={panSeconds}
              onChange={(e) => setPanSeconds(parseInt(e.target.value, 10) || 15)}
              className="mt-1 w-full border border-app rounded px-3 py-2 text-sm bg-app font-mono"
              data-testid="reels-pan-seconds-input"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-widest text-text-muted">Slutkort — rubrik</label>
            <input
              type="text"
              value={videoTitle}
              onChange={(e) => setVideoTitle(e.target.value)}
              maxLength={140}
              className="mt-1 w-full border border-app rounded px-3 py-2 text-sm bg-app"
              data-testid="reels-video-title-input"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-widest text-text-muted">Slutkort — underrubrik</label>
            <input
              type="text"
              value={videoSubtitle}
              onChange={(e) => setVideoSubtitle(e.target.value)}
              maxLength={200}
              className="mt-1 w-full border border-app rounded px-3 py-2 text-sm bg-app"
              data-testid="reels-video-subtitle-input"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-widest text-text-muted">IG-bildtext</label>
            <textarea
              value={videoCaption}
              onChange={(e) => setVideoCaption(e.target.value)}
              placeholder="Bildtext på IG… länk, hashtags"
              rows={3}
              maxLength={2200}
              className="mt-1 w-full border border-app rounded px-3 py-2 text-sm bg-app"
              data-testid="reels-video-caption-input"
            />
            <div className="text-[10px] text-text-muted text-right mt-0.5">{videoCaption.length}/2200</div>
          </div>
        </div>

        <div className="flex gap-2 mt-3 flex-wrap">
          <button
            onClick={() => renderVideo({ dryRun: true })}
            disabled={rendering}
            className="border border-app hover:bg-surface-hover rounded-md px-4 py-2 text-sm disabled:opacity-50"
            data-testid="reels-video-dryrun-btn"
          >
            {rendering ? "Renderar…" : "Rendera dry-run"}
          </button>
          <button
            onClick={() => {
              if (!window.confirm(`Publicera Reel till @trappkonsulten__ nu?\n\nRubrik: ${videoTitle}\nLandningssida: ${videoSlug}`)) return;
              renderVideo({ dryRun: false });
            }}
            disabled={rendering}
            className="bg-brand hover:bg-brand-hover text-white rounded-md px-4 py-2 text-sm font-medium disabled:opacity-50"
            data-testid="reels-video-publish-btn"
          >
            {rendering ? "Kör…" : "Rendera + publicera Reel"}
          </button>
        </div>

        {lastRender?.video_url && (
          <div className="mt-4 bg-app border border-app rounded p-3 text-sm" data-testid="reels-last-render">
            <div className="text-xs uppercase tracking-widest text-text-muted mb-2">Senast renderad video</div>
            <video
              src={lastRender.video_url}
              controls
              className="w-full max-w-xs rounded"
              style={{ aspectRatio: "9/16" }}
            />
            <div className="mt-2 flex gap-3 text-xs">
              <a href={lastRender.video_url} target="_blank" rel="noreferrer" className="text-brand hover:underline">Öppna MP4 ↗</a>
              {lastRender.permalink && (
                <a href={lastRender.permalink} target="_blank" rel="noreferrer" className="text-brand hover:underline">Öppna på IG ↗</a>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Batch: publicera alla 10 branscher */}
      <div className="bg-surface border border-app rounded-lg p-5" data-testid="reels-batch-panel">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <h4 className="text-base font-semibold text-text-primary">
              🚀 Publicera 10 reels — en per bransch
            </h4>
            <p className="text-sm text-text-muted mt-1">
              Batch-jobb: renderar och publicerar en Instagram Reel per B2B-bransch
              (bygg, snickeri, vvs, måleri, fastighetsservice, mark, golv, el, fasad, städ).
              Krävs för att kunna bygga Meta Ads-kampanjen. Tar ca 15-20 min i bakgrunden.
            </p>
          </div>
          <span
            className={`text-xs px-2 py-1 rounded ${
              batchRunning
                ? "bg-amber-100 text-amber-800 animate-pulse"
                : "bg-slate-100 text-slate-700"
            }`}
            data-testid="reels-batch-status"
          >
            {batchRunning ? "Kör…" : "Inaktiv"}
          </span>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={batchSkipExisting}
              onChange={(e) => setBatchSkipExisting(e.target.checked)}
              disabled={batchRunning}
              data-testid="reels-batch-skip-existing"
            />
            <span>Hoppa över redan publicerade</span>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={batchDryRun}
              onChange={(e) => setBatchDryRun(e.target.checked)}
              disabled={batchRunning}
              data-testid="reels-batch-dry-run"
            />
            <span>Dry-run (rendera utan att publicera)</span>
          </label>
          <div>
            <label className="text-xs uppercase tracking-widest text-text-muted">
              Väntetid mellan reels (sek)
            </label>
            <input
              type="number"
              min={10}
              max={600}
              value={batchDelaySec}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "") { setBatchDelaySec(""); return; }
                const n = parseInt(v, 10);
                if (!isNaN(n)) setBatchDelaySec(n);
              }}
              onBlur={() => {
                if (batchDelaySec === "" || batchDelaySec < 10) setBatchDelaySec(60);
              }}
              disabled={batchRunning}
              className="mt-1 w-full border border-app rounded px-3 py-2 text-sm bg-app font-mono"
              data-testid="reels-batch-delay-input"
            />
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <button
            onClick={publishAllIndustries}
            disabled={batchRunning}
            className="bg-brand hover:bg-brand-hover text-white rounded-md px-4 py-2 text-sm font-medium disabled:opacity-50"
            data-testid="reels-batch-run-btn"
          >
            {batchRunning ? "Kör i bakgrunden…" : "🚀 Publicera alla 10 branscher"}
          </button>
          {batchRunning && (
            <button
              onClick={() => { setBatchRunning(false); toast.info("Auto-refresh stoppad — batch fortsätter i bakgrunden"); }}
              className="border border-app hover:bg-surface-hover rounded-md px-3 py-2 text-sm"
              data-testid="reels-batch-stop-poll-btn"
            >
              Stoppa auto-refresh
            </button>
          )}
        </div>

        {batchInfo && (
          <div className="mt-3 text-xs bg-app border border-app rounded p-3">
            <div className="text-text-muted">
              Batch startad: <span className="font-mono">{batchInfo.count} branscher</span>,
              uppskattad tid: <span className="font-mono">~{batchInfo.estimated_minutes} min</span>
            </div>
            <div className="mt-1 text-text-primary">
              {batchInfo.industries.join(" · ")}
            </div>
          </div>
        )}

        <div className="mt-3 text-[11px] text-text-muted italic">
          💡 Tips: När alla 10 är publicerade — aktivera <b>Instagram → Facebook Reels crossposting</b> i
          Meta Business Suite (Content → Reels → Settings) så speglas allt automatiskt till Facebook-sidan.
        </div>
      </div>

      {/* Testposta bild */}
      <div className="bg-surface border border-app rounded-lg p-5">
        <h4 className="text-base font-semibold text-text-primary">Testposta bild till Instagram</h4>
        <p className="text-sm text-text-muted mt-1">
          Klistra in en publik <span className="font-mono">https://</span>-URL till en JPG/PNG (min 320×320,
          rekommenderat 1080×1080 eller 1080×1350). Bilden postas som feed-inlägg på
          det anslutna kontot.
        </p>

        <div className="mt-4 space-y-3">
          <div>
            <label className="text-xs uppercase tracking-widest text-text-muted">Bild-URL</label>
            <input
              type="url"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="https://example.com/photo.jpg"
              className="mt-1 w-full border border-app rounded px-3 py-2 text-sm bg-app font-mono"
              data-testid="reels-image-url-input"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-text-muted">Bildtext (valfri)</label>
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Skriv en bildtext… #trappor #hantverk"
              rows={3}
              maxLength={2200}
              className="mt-1 w-full border border-app rounded px-3 py-2 text-sm bg-app"
              data-testid="reels-caption-input"
            />
            <div className="text-[10px] text-text-muted text-right mt-0.5">{caption.length}/2200</div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={publishImage}
              disabled={posting}
              className="bg-brand hover:bg-brand-hover text-white rounded-md px-4 py-2 text-sm font-medium disabled:opacity-50"
              data-testid="reels-publish-image-btn"
            >
              {posting ? "Publicerar…" : "Publicera bild nu"}
            </button>
            <button
              onClick={() => { setImageUrl(""); setCaption(""); }}
              className="border border-app hover:bg-surface-hover rounded-md px-3 py-2 text-sm"
              data-testid="reels-clear-btn"
            >
              Rensa
            </button>
          </div>
        </div>
      </div>

      {/* Historik */}
      <div className="bg-surface border border-app rounded-lg p-5">
        <div className="flex items-center justify-between">
          <h4 className="text-base font-semibold text-text-primary">Publiceringshistorik</h4>
          <button
            onClick={loadJobs}
            className="text-xs text-text-muted hover:text-text-primary"
            data-testid="reels-refresh-btn"
          >
            {loading ? "Uppdaterar…" : "↻ Uppdatera"}
          </button>
        </div>

        {jobs.length === 0 ? (
          <div className="mt-3 text-sm text-text-muted italic">
            Inga jobb ännu. Kör en testpost ovan för att komma igång.
          </div>
        ) : (
          <div className="mt-3 divide-y divide-app">
            {jobs.map((j) => {
              const st = STATUS_STYLES[j.status] || STATUS_STYLES.queued;
              return (
                <div key={j.id} className="py-3 flex items-start gap-3" data-testid={`reels-job-${j.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[11px] px-2 py-0.5 rounded font-medium ${st.cls}`}>{st.label}</span>
                      <span className="text-[11px] text-text-muted uppercase tracking-widest">{j.kind}</span>
                      <span className="text-[11px] text-text-muted font-mono">{j.id}</span>
                    </div>
                    {j.caption_preview && (
                      <div className="text-sm text-text-primary mt-1 truncate">{j.caption_preview}</div>
                    )}
                    {j.media_url && (
                      <div className="text-[11px] text-text-muted font-mono truncate mt-0.5">{j.media_url}</div>
                    )}
                    {j.error && (
                      <div className="text-[11px] text-red-700 mt-1 leading-tight">Fel: {j.error}</div>
                    )}
                    <div className="text-[10px] text-text-muted mt-1">
                      {j.published_at
                        ? `Publicerad ${new Date(j.published_at).toLocaleString("sv-SE")}`
                        : `Skapad ${new Date(j.created_at).toLocaleString("sv-SE")}`}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 items-end">
                    {j.permalink && (
                      <a
                        href={j.permalink}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[11px] text-brand hover:underline"
                        data-testid={`reels-open-${j.id}`}
                      >
                        Öppna på IG ↗
                      </a>
                    )}
                    <button
                      onClick={() => deleteJob(j.id)}
                      className="text-[11px] text-red-600 hover:underline"
                      data-testid={`reels-delete-${j.id}`}
                    >
                      Ta bort
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
